import React from "react";
import {
  Autocomplete,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  TextField,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { useAppStore } from "../../../appStore";
import { useEditCameraStore } from "./editCameraStore/editCameraStore";
import SelectedStreamDropDown from "./SelectedStreamDropDown";
import { usePatriotIgnoreZonesOnAlertPage } from "../../common/PatriotDrawIgnoreZonesGate";
import {
  PatriotDeliveryChoice,
  PatriotDeliveryValues,
} from "../../common/PatriotDeliveryChoice";
import {
  AlertReceiverCard,
  ConnectionDetails,
  DetailItem,
  ReceiverSection,
} from "./alertDelivery/AlertReceiverCard";
import { DrawIgnoreZonesChoice } from "./alertDelivery/DrawIgnoreZonesChoice";
import {
  RECEIVER_CONFIGS,
  RECEIVER_ORDER_AFTER_WEBHOOKS,
  RECEIVER_ORDER_BEFORE_WEBHOOKS,
  ReceiverField,
  ReceiverKey,
  SUREVIEW_SERVER_OPTIONS,
  isOn,
  streamAlerts,
  sureviewServerHost,
  sureviewServerName,
} from "./alertDelivery/receiverConfigs";
import {
  describeReceiver,
  describeWebhook,
} from "./alertDelivery/receiverDescriptions";
import { useAlertReceiverEditing } from "./alertDelivery/useAlertReceiverEditing";

// Edit mode of the Alert Settings tab (ENG-2483). Same cards as the view, with
// the "what is sent" options as labelled choices and the connection fields
// editable. Every control writes the same fields, through the same store
// state, as the old per-integration table did (see useAlertReceiverEditing),
// so the PATCH bodies and markSiteUnsynced-on-save are unchanged.

const SecretInput = ({
  id,
  label,
  value,
  onChange,
  onBlur,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
}) => {
  const [shown, setShown] = React.useState(false);
  return (
    <FormControl variant="standard" fullWidth>
      <InputLabel htmlFor={id}>{label}</InputLabel>
      <Input
        id={id}
        type={shown ? "text" : "password"}
        value={value}
        onChange={(event) => {
          const nextValue = event.target.value;
          onChange(nextValue);
        }}
        onBlur={onBlur}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={() => setShown((wasShown) => !wasShown)}
              onMouseDown={(event) => event.preventDefault()}
              edge="end"
            >
              {shown ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        }
      />
    </FormControl>
  );
};

export const AlertSettingsTabEditMode = ({
  setEditStreamWebhookDialogOpen,
  immixAlertsOnCamera,
  diceAlertsOnCamera,
  sureViewAlertsOnCamera,
  siteInfo,
  webhooksAlertsOnCamera,
  sentinelAlertsOnCamera,
  boldAlertsOnCamera,
  patriotAlertsOnCamera,
  softguardAlertsOnCamera,
  evalinkAlertsOnCamera,
  cameraGeneralInfo,
  setPatchObject,
  setAddStreamWebhookDialogOpen,
}: {
  immixAlerts: any;
  diceAlerts: any;
  sureviewAlerts: any;
  setEditStreamWebhookDialogOpen: any;
  sentinelAlerts: any;
  boldAlerts: any;
  patriotAlerts: any;
  softguardAlerts: any;
  immixAlertsOnCamera: boolean;
  diceAlertsOnCamera: boolean;
  sureViewAlertsOnCamera: boolean;
  siteInfo: any;
  webhooksAlertsOnCamera: boolean;
  sentinelAlertsOnCamera: boolean;
  boldAlertsOnCamera: boolean;
  patriotAlertsOnCamera: boolean;
  softguardAlertsOnCamera: boolean;
  evalinkAlertsOnCamera: boolean;
  cameraGeneralInfo: any;
  setPatchObject: any;
  setGeneralInfoChangesMade: any;
  setAddStreamWebhookDialogOpen: any;
}) => {
  const { userPermissions } = useAppStore();
  const ignoreZonesOnAlertPage = usePatriotIgnoreZonesOnAlertPage();

  const {
    setDeleteWebhookDialogOpen,
    setWebhookIdToDelete,
    webhooksData,
    cameraStreams,
    cameraStreamsSelectedIndex,
    cameraStreamsPatchObject,
    sysaidAlertsOnCamera,
  } = useEditCameraStore();

  const { localAlert, changeAlert, blurField, commitAlert } =
    useAlertReceiverEditing();

  React.useEffect(() => {
    setPatchObject((previousState: any) => {
      return { ...previousState, streams: cameraGeneralInfo.streams };
    });
  }, []);

  const onCamera: Record<ReceiverKey, boolean | undefined> = {
    immix: immixAlertsOnCamera,
    dice: diceAlertsOnCamera,
    sureview: sureViewAlertsOnCamera,
    sentinel: sentinelAlertsOnCamera,
    bold: boldAlertsOnCamera,
    patriot: patriotAlertsOnCamera,
    softguard: softguardAlertsOnCamera,
    evalink: evalinkAlertsOnCamera,
    sysaid: sysaidAlertsOnCamera,
  };

  const renderField = (
    key: ReceiverKey,
    field: ReceiverField,
    alert: any,
    alertIndex: number
  ) => {
    const local = localAlert(key, alert.id);
    const inputId = `${key}-${alert.id}-${field.key}`;
    const onBlur = () => blurField(key, alert.id, alertIndex, field.key);

    if (field.kind === "readOnly") {
      return (
        <DetailItem key={field.key} label={field.label}>
          {alert[field.key] || "--"}
        </DetailItem>
      );
    }
    if (field.kind === "secret") {
      return (
        <SecretInput
          key={field.key}
          id={inputId}
          label={field.label}
          value={local?.[field.key] || ""}
          onChange={(nextValue) =>
            changeAlert(key, alert.id, { [field.key]: nextValue })
          }
          onBlur={onBlur}
        />
      );
    }
    if (field.kind === "sureviewServer") {
      return (
        <Autocomplete
          key={field.key}
          disablePortal
          id={inputId}
          options={SUREVIEW_SERVER_OPTIONS.map(([label]) => label)}
          value={sureviewServerName(local?.[field.key] || "") || null}
          onChange={(event: any, newValue: any) => {
            const host = sureviewServerHost(newValue);
            // The PATCH body gets the raw selection (undefined when cleared);
            // the local copy keeps "" (unchanged from the old table).
            changeAlert(
              key,
              alert.id,
              { [field.key]: host },
              { [field.key]: host || "" }
            );
          }}
          onBlur={onBlur}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label={field.label}
              autoComplete="off"
            />
          )}
        />
      );
    }
    return (
      <TextField
        key={field.key}
        id={inputId}
        label={field.label}
        value={local?.[field.key] || ""}
        variant="standard"
        fullWidth
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const nextValue = event.target.value;
          changeAlert(key, alert.id, { [field.key]: nextValue });
        }}
        onBlur={onBlur}
      />
    );
  };

  const renderDeliveryChoice = (
    key: ReceiverKey,
    alert: any,
    alertIndex: number
  ) => {
    const config = RECEIVER_CONFIGS[key];
    const local = localAlert(key, alert.id);
    if (config.delivery === "patriot") {
      return (
        <div className="mt-2">
          <PatriotDeliveryChoice
            name={`patriot-delivery-${alert.id}`}
            sendVideo={local?.patriot_send_video}
            drawIgnoreZones={local?.draw_ignore_zones}
            onChange={(values: PatriotDeliveryValues) =>
              commitAlert(key, alert.id, alertIndex, { ...values })
            }
          />
        </div>
      );
    }
    if (config.delivery === "drawIgnoreZones") {
      return (
        <DrawIgnoreZonesChoice
          receiver={key}
          name={`${key}-draw-ignore-zones-${alert.id}`}
          value={isOn(local?.draw_ignore_zones)}
          onChange={(checked) =>
            commitAlert(key, alert.id, alertIndex, {
              draw_ignore_zones: checked,
            })
          }
        />
      );
    }
    return null;
  };

  const renderReceiver = (key: ReceiverKey) => {
    if (!onCamera[key]) return null;
    const config = RECEIVER_CONFIGS[key];
    const alerts =
      streamAlerts(
        cameraStreamsPatchObject?.[cameraStreamsSelectedIndex],
        config.alertsKey
      ) ?? [];
    return (
      <ReceiverSection
        key={key}
        title={`${config.name} Alerts`}
        testId={`alert-receiver-section-${key}`}
      >
        {alerts.map((alert: any, index: number) => {
          // The headline follows the edits as they are made.
          const { headline, note } = describeReceiver(
            key,
            { ...alert, ...localAlert(key, alert.id) },
            { ignoreZonesOnAlertPage }
          );
          return (
            <AlertReceiverCard
              key={alert.id}
              label={`${config.name} Alert ${index + 1}`}
              headline={headline}
              note={note}
              testId={`alert-receiver-card-${key}-${alert.id}`}
            >
              {renderDeliveryChoice(key, alert, index)}
              <ConnectionDetails idPrefix={`${key}-${alert.id}`} defaultOpen>
                {config.fields.map((field) =>
                  renderField(key, field, alert, index)
                )}
              </ConnectionDetails>
            </AlertReceiverCard>
          );
        })}
      </ReceiverSection>
    );
  };

  const streamWebhooks: any[] = (webhooksData || []).filter(
    (object: any) =>
      cameraStreams &&
      cameraStreams[cameraStreamsSelectedIndex] &&
      object.stream.id === cameraStreams[cameraStreamsSelectedIndex].id
  );

  return (
    <div className="relative w-full">
      {cameraStreams && cameraStreams.length > 1 && (
        <div className="bg-white">
          <SelectedStreamDropDown />
        </div>
      )}
      {RECEIVER_ORDER_BEFORE_WEBHOOKS.map(renderReceiver)}
      {(webhooksData || siteInfo.webhooks || webhooksAlertsOnCamera) && (
        <ReceiverSection
          title="Webhooks"
          testId="alert-receiver-section-webhooks"
          addButton={
            userPermissions.edit_cameras ? (
              <OrangeTextButton
                onClick={() => setAddStreamWebhookDialogOpen(true)}
                text="+ Add Stream Webhook"
                disabled={false}
              />
            ) : undefined
          }
        >
          {streamWebhooks.map((object: any, index: number) => {
            const { headline, note } = describeWebhook(object);
            return (
              <AlertReceiverCard
                key={object.id}
                label={`Stream Webhook ${index + 1}`}
                headline={headline}
                note={note}
                testId={`alert-receiver-card-webhook-${object.id}`}
                actions={
                  <>
                    <OrangeTextButton
                      onClick={() => {
                        localStorage.setItem(
                          "selectedWebhook",
                          JSON.stringify(object)
                        );
                        setEditStreamWebhookDialogOpen(true);
                      }}
                      text={
                        <EditIcon
                          fontSize="small"
                          aria-label={`Edit Stream Webhook ${index + 1}`}
                        />
                      }
                      disabled={false}
                    />
                    <OrangeTextButton
                      onClick={() => {
                        setDeleteWebhookDialogOpen(true);
                        setWebhookIdToDelete(object.id);
                      }}
                      text={
                        <DeleteIcon
                          fontSize="small"
                          aria-label={`Delete Stream Webhook ${index + 1}`}
                        />
                      }
                      disabled={false}
                    />
                  </>
                }
              >
                <ConnectionDetails idPrefix={`webhook-${object.id}`}>
                  <DetailItem label="Webhook">
                    {object.webhook?.display_name || "--"}
                  </DetailItem>
                  <DetailItem label="Custom fields">
                    {object.custom_fields
                      ? JSON.stringify(object.custom_fields)
                      : "--"}
                  </DetailItem>
                </ConnectionDetails>
              </AlertReceiverCard>
            );
          })}
        </ReceiverSection>
      )}
      {RECEIVER_ORDER_AFTER_WEBHOOKS.map(renderReceiver)}
    </div>
  );
};
